/**
 * TaskHeader - Shared header component for task interaction views.
 *
 * Displays task title, description, and optional metadata chips.
 */

import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import BlockIcon from '@mui/icons-material/Block';
import { colors } from '../../theme/colors';

export type TaskStatus = 'not_started' | 'in_progress' | 'completed' | 'blocked' | 'ready';

export interface TaskHeaderProps {
  title: string;
  description?: string;
  status?: TaskStatus;
  showStatus?: boolean;
  chips?: Array<{ label: string; color?: string; bgcolor?: string }>;
  actions?: React.ReactNode;
}

const STATUS_CONFIG: Record<TaskStatus, { label: string; color: string; bgcolor: string; Icon: React.ComponentType<{ sx?: object }> }> = {
  not_started: { label: 'Not Started', color: colors.gray[500], bgcolor: colors.gray[100], Icon: PendingIcon },
  in_progress: { label: 'In Progress', color: colors.blue[600], bgcolor: colors.blue[50], Icon: PendingIcon },
  completed: { label: 'Done', color: colors.green[600], bgcolor: colors.green[50], Icon: CheckCircleIcon },
  blocked: { label: 'Blocked', color: colors.red[600], bgcolor: colors.red[50], Icon: BlockIcon },
  ready: { label: 'Ready', color: colors.green[600], bgcolor: colors.green[50], Icon: CheckCircleIcon },
};

export const TaskHeader: React.FC<TaskHeaderProps> = ({
  title,
  description,
  status,
  showStatus = true,
  chips = [],
  actions,
}) => {
  const statusConfig = status ? STATUS_CONFIG[status] : null;
  const StatusIcon = statusConfig?.Icon;

  return (
    <Box
      sx={{
        p: 2,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: colors.gray[50],
      }}
    >
      {/* Title row */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: description ? 1 : 0 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, flex: 1, lineHeight: 1.3 }}>
          {title}
        </Typography>

        {/* Status and custom chips */}
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, alignItems: 'center' }}>
          {actions}
          {showStatus && statusConfig && StatusIcon && (
            <Chip
              icon={<StatusIcon sx={{ fontSize: 14 }} />}
              label={statusConfig.label}
              size="small"
              sx={{
                height: 24,
                bgcolor: statusConfig.bgcolor,
                color: statusConfig.color,
                fontWeight: 500,
                '& .MuiChip-icon': { color: statusConfig.color },
              }}
            />
          )}
          {chips.map((chip, idx) => (
            <Chip
              key={idx}
              label={chip.label}
              size="small"
              sx={{
                height: 24,
                bgcolor: chip.bgcolor || colors.gray[100],
                color: chip.color || colors.textSecondary,
                fontWeight: 500,
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Description */}
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {description}
        </Typography>
      )}
    </Box>
  );
};

export default TaskHeader;
